/* eslint-disable max-params */
import React, { useEffect, useState, useCallback, useMemo, useImperativeHandle } from 'react';

import Item from '../item';
import Empty from '../../empty';
import { ICascaderItem, AnyObjType, ICascaderGroupRefProps } from '../interface';
import {
  getParentIdList,
  getAllValueList,
  getAllChildrenList,
  getDeleteAllChildrenValue,
  getAddedList,
  getAllValueListByAllChildren,
  getIndeterminateList,
  getSelectedListByValue
} from '../modules/util';
import { valueType } from '../modules/valueType';

import style from './index.module.less';

const FIRSTVALUE = 'FIRST_VALUE';

function unique(arr: (string | number)[]) {
  const res: Array<string | number> = [];
  const obj: AnyObjType = {};
  for (const item of arr) {
    if (!obj[item]) {
      obj[item] = 1;
      res.push(item);
    }
  }
  return res;
}

export interface Prop {
  multiple?: boolean;
  treeData: ICascaderItem[];
  searchList?: ICascaderItem[];
  ref?: React.RefObject<ICascaderGroupRefProps>;
  onChange: (
    value: string | number | Array<string | number>,
    label?: string | number | AnyObjType,
    deep?: number,
    data?: AnyObjType,
    checked?: boolean
  ) => void;
  showCheckedStrategy?: string;
  value: (string | number)[] | string | number;
  visible?: boolean;
  itemWidth?: number;
  renderNode?: (
    label: string | number,
    value: (string | number)[] | string | number,
    data: AnyObjType
  ) => React.ReactNode;
  onContentChange?: () => void;
  // 选中就赋值
  changeOnSelect?: boolean;
  notFoundContent?: string;
  search?: boolean;
  // 忽略层级接口，不在意父子节点关系
  checkStrictly?: boolean;
  // 是否启用虚拟化列表
  isVirtualized?: boolean;
  virtualizeWidth?: number;
  virtualizeHeight?: number;
  disabledCheckBox?: boolean;
  placeholder?: string;
  className?: string;
}

interface GroupItem {
  value: string | number;
  deep: number;
  list: ICascaderItem[];
}

// interface VirtualRowItem {
//   index: number;
//   key: string;
//   style: object;
// }

const emptyArr: any[] = [];
// 稳定的空函数引用，避免内联 () => null 每次渲染都创建新引用导致 effect 无限触发
const noop = () => null;

const ListGroup = React.forwardRef<ICascaderGroupRefProps, Prop>((props, ref) => {
  const {
    treeData = emptyArr,
    multiple = false,
    onChange = noop,
    showCheckedStrategy = 'SHOW_PARENT',
    value = '',
    visible = false,
    itemWidth = 220,
    renderNode = undefined,
    onContentChange = noop,
    changeOnSelect = true,
    notFoundContent = '暂无数据',
    searchList = emptyArr,
    search = false,
    checkStrictly = true,
    isVirtualized = false,
    virtualizeWidth = 220,
    virtualizeHeight = 292,
    disabledCheckBox = false,
    placeholder
  }: Prop = props;
  const [list, setList] = useState<GroupItem[]>(emptyArr);
  const [visibleFlag, setVisibleFlag] = useState<boolean>(false);
  const [treeValue, setTreeValue] = useState<Array<string | number>>(emptyArr);

  useEffect(() => {
    if (search) {
      if (searchList?.length) {
        setList([
          {
            value: FIRSTVALUE,
            deep: 0,
            list: searchList.map(item => {
              return { ...item };
            })
          }
        ]);
      } else {
        setList(emptyArr);
      }
    } else if (treeData.length) {
      setList([
        {
          value: FIRSTVALUE,
          deep: 0,
          list: treeData.map(item => {
            return { ...item };
          })
        }
      ]);
    } else {
      setList(emptyArr);
    }
  }, [searchList, search, treeData]);

  useEffect(() => {
    if (onContentChange && typeof onContentChange === 'function') {
      onContentChange();
    }
  }, [list, onContentChange]);

  useEffect(() => {
    if (!multiple) {
      if (value && visibleFlag !== visible && visible) {
        setVisibleFlag(visible);
        setList(getSelectedListByValue(treeData, value));
      }
    } else {
      const valueList = value ? (Array.isArray(value) ? value : [value]) : emptyArr;
      if (!checkStrictly) {
        // 用函数式更新避免内容相同时触发重渲染
        setTreeValue(prev => {
          if (prev === valueList) return prev;
          if (prev.length === valueList.length && prev.every((v, i) => v === (valueList as (string | number)[])[i])) return prev;
          return valueList as (string | number)[];
        });
        return;
      }
      // 用函数式更新：内容相同时返回 prev，防止新引用触发无限重渲染
      const stableSet = (next: (string | number)[]) => {
        setTreeValue(prev => {
          if (prev.length === next.length && prev.every((v, i) => v === next[i])) return prev;
          return next;
        });
      };
      if (showCheckedStrategy === valueType.SHOW_PARENT) {
        stableSet(getAllValueList(valueList, treeData));
      } else if (showCheckedStrategy === valueType.SHOW_ALL) {
        stableSet(getAllValueList(valueList, treeData));
      } else if (showCheckedStrategy === valueType.SHOW_CHILD) {
        stableSet(getAllValueListByAllChildren(valueList, treeData));
      }
    }
  }, [value, multiple, treeData, visible, visibleFlag, showCheckedStrategy, checkStrictly]);

  useEffect(() => {
    setVisibleFlag(!!visible);
  }, [visible]);

  const onHover = useCallback(
    (value: any, children = emptyArr, deep = 0) => {
      const hasValue = list.some(item => item.value === value);
      if (hasValue) {
        return;
      }
      // 如果没有children，就要删除下一级
      if (!children.length) {
        const newList = list.filter(item => item.deep <= deep);
        setList(newList);
        return;
      }
      const nextDeep = deep + 1;
      const doubleIndex = list.findIndex(item => item.deep === nextDeep);

      // 如果有相同deep的，就替换，并且把后面的都删除掉
      if (doubleIndex > -1) {
        // 排除所有deep一样的
        list[doubleIndex] = {
          value,
          deep: nextDeep,
          list: children
        };
        // 去掉所有deep比nextDeep大的项
        const newList = list.filter(item => item.deep <= nextDeep);
        setList(newList);
        return;
      }
      // 没有就添加到后面
      list.push({
        value,
        deep: nextDeep,
        list: children
      });
      setList([...list]);
    },
    [list, checkStrictly]
  );

  const onItemClick = useCallback(
    (val: any, label: any, deep: any, data: any) => {
      onChange(val, label, deep, data);
    },
    [onChange]
  );

  // 多选的时候触发
  const onItemChange = useCallback(
    (checked: boolean, val: string | number, data?: AnyObjType, deep?: number, label?: string | number): void => {
      let newValue: Array<string | number> = unique(treeValue);
      let list: Array<string | number> = [];
      // 如果忽略了父子关系
      if (!checkStrictly) {
        if (checked) {
          newValue = newValue.concat(val);
        } else {
          const deleteIndex = newValue.findIndex(item => item === val);
          if (deleteIndex > -1) {
            newValue.splice(deleteIndex, 1);
          }
        }
        if (!newValue.length) {
          setTreeValue(emptyArr);
          onChange(emptyArr, label, deep, data, checked);
          return;
        }
        setTreeValue([...newValue]);
        onChange([...newValue], label, deep, data, checked);
        return;
      }
      if (checked) {
        newValue = getAddedList(val, treeData, newValue);
      } else {
        newValue = getDeleteAllChildrenValue(val, treeData, newValue);
      }
      if (!newValue.length) {
        setTreeValue(emptyArr);
        onChange(emptyArr, label, deep, data, checked);
        return;
      }

      if (showCheckedStrategy === valueType.SHOW_PARENT) {
        list = getParentIdList(newValue, treeData);
      } else if (showCheckedStrategy === valueType.SHOW_ALL) {
        list = getAllValueList(newValue, treeData);
      } else if (showCheckedStrategy === valueType.SHOW_CHILD) {
        list = getAllChildrenList(newValue, treeData);
      }
      onChange(list || emptyArr, label, deep, data, checked);
      if (!list) {
        setTreeValue(emptyArr);
      } else {
        setTreeValue(newValue);
      }
    },
    [value, showCheckedStrategy, treeData, treeValue, onChange, checkStrictly]
  );

  useImperativeHandle(
    ref,
    () => {
      return {
        onDeleteItem: (val: string | number): void => {
          onItemChange(false, val);
        }
      };
    },
    [onItemChange]
  );

  const indeterminateList: (number | string)[] = useMemo(() => {
    return getIndeterminateList(treeValue, treeData);
  }, [treeValue, treeData]);

  // const rowRenderer = (
  //   key: string,
  //   style: object,
  //   item: ICascaderItem,
  //   index: number,
  //   copyValueList: (string | number)[]
  // ) => {
  //   const { children = [], label, title, data }: ICascaderItem = item;
  //   let deep: number = index;
  //   if (item.deep || item.deep === 0) {
  //     deep = item.deep;
  //   }
  //   let selected = false;
  //   let indeterminate = false;
  //   let active = false;
  //   if (!multiple) {
  //     selected = value === item.value;
  //   } else {
  //     selected = treeValue.includes(item.value);
  //   }
  //   if (copyValueList.includes(item.value)) {
  //     active = true;
  //   }
  //   if (selected) {
  //     active = false;
  //   } else if (multiple) {
  //     if (indeterminateList.includes(item.value) && checkStrictly) {
  //       indeterminate = true;
  //     }
  //   }
  //   return (
  //     <div style={style} key={key}>
  //       <Item
  //         renderNode={renderNode}
  //         indeterminate={indeterminate}
  //         active={active}
  //         selected={selected}
  //         // eslint-disable-next-line react/no-children-prop
  //         children={children}
  //         value={item.value}
  //         data={data || {}}
  //         deep={deep}
  //         onHover={onHover}
  //         label={label || title || ''}
  //         key={item.value}
  //         multiple={!!multiple}
  //         onClick={onItemClick}
  //         onChange={onItemChange}
  //         itemWidth={itemWidth || 0}
  //         changeOnSelect={!!changeOnSelect}
  //         disabledCheckBox={disabledCheckBox}
  //       />
  //     </div>
  //   );
  // };

  /* <List
                width={virtualizeWidth}
                height={virtualizeHeight}
                rowCount={firstList.length}
                rowHeight={32.81}
                rowRenderer={({ index: idx, key, style }: VirtualRowItem) =>
                  rowRenderer(key, style, firstList[idx], index, copyValueList)
                }
              /> */

  const ListEle: React.ReactNode = useMemo(() => {
    if (!list.length) {
      return <Empty icon='ecoboost-iconfontemptypag_tickets_02' mode='height' description={notFoundContent} />;
    }
    const copyList: GroupItem[] = [...list];
    const copyValueList: (string | number)[] = copyList.map(item => item.value);
    return list.map((item: GroupItem, index) => {
      const firstList: ICascaderItem[] = item.list;
      return (
        <div className={style.list} key={`${index}`}>
          <ul>
            {isVirtualized
              ? null
              : firstList.map((item: ICascaderItem) => {
                  const { children = [], label, title, data }: ICascaderItem = item;
                  let deep: number = index;
                  if (item.deep || item.deep === 0) {
                    deep = item.deep;
                  }
                  let selected = false;
                  let indeterminate = false;
                  let active = false;
                  if (!multiple) {
                    selected = value === item.value;
                  } else {
                    selected = treeValue.includes(item.value);
                  }
                  if (copyValueList.includes(item.value)) {
                    active = true;
                  }
                  if (selected) {
                    active = false;
                  } else if (multiple) {
                    if (indeterminateList.includes(item.value) && checkStrictly) {
                      indeterminate = true;
                    }
                  }
                  return (
                    <Item
                      renderNode={renderNode}
                      indeterminate={indeterminate}
                      active={active}
                      selected={selected}
                      // eslint-disable-next-line react/no-children-prop
                      children={children}
                      value={item.value}
                      data={data || {}}
                      deep={deep}
                      onHover={onHover}
                      label={label || title || ''}
                      key={item.value}
                      multiple={!!multiple}
                      onClick={onItemClick}
                      onChange={onItemChange}
                      itemWidth={itemWidth || 0}
                      changeOnSelect={!!changeOnSelect}
                      disabledCheckBox={disabledCheckBox}
                    />
                  );
                })}
          </ul>
        </div>
      );
    });
  }, [
    list,
    multiple,
    onHover,
    onItemClick,
    value,
    indeterminateList,
    checkStrictly,
    isVirtualized,
    virtualizeWidth,
    virtualizeHeight
  ]);

  return <div className={`${style.clearfix} ${!list.length ? style.empty : ''}`}>{ListEle}</div>;
});

export default ListGroup;
